import { useState, useEffect, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import Select from "react-select";
import { useAppData } from "../../../context/AppDataContext";
import { useAuthInfo } from "../../../context/AuthContext";
import { errorToast, successfulToast } from "../../../util/toastNotifications";
import Loading from "../../layout/Loading";
import Form from "../../layout/Form";
import FormFieldText from "../../input/FormFieldText";
import FormFieldTags from "../../input/FormFieldTags";
import Button from "../../core/Button";
import NovelEditor from "../../core/NovelEditor";
import ColorPicker from "../../core/ColorPicker";
import ComboBox from "../../core/ComboBox";

const getInitialForm = () => {
  const today = new Date().toISOString().split("T")[0];
  return {
    name: "",
    description: "",
    start_date: today,
    due_date: "",
    company_id: "",
    contact_id: "",
    status_id: "",
    tags: [],
    color: "#FFFFFF",
    project_manager_id: "",
    product_owner_id: "",
  };
};

const ProjectForm = () => {
  const { projectId } = useParams();
  const history = useHistory();
  const { api } = useAppData();
  const { userInfo } = useAuthInfo();
  const [formFields, setFormFields] = useState(getInitialForm());
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [showStatusComboBox, setShowStatusComboBox] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [companiesData, contactsData, statusesData, usersData] =
          await Promise.all([
            api.company.getCompanies(),
            api.contact.getContacts(),
            api.projectStatus.getProjectStatuses(),
            api.user.getUsers(),
          ]);

        setCompanies(companiesData);
        setContacts(contactsData);
        const orgStatuses = statusesData.filter(
          (status) => status.org_id === userInfo?.org_id
        );

        setStatuses(orgStatuses);
        setUsers(usersData);

        if (projectId) {
          const projectData = await api.project.getProjectById(projectId);
          const formatDateForInput = (dateString) => {
            if (!dateString) return "";
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
          };
          setFormFields({
            name: projectData.name,
            description: projectData.description || "",
            project_manager_id: projectData.project_manager?.user_id || "",
            product_owner_id: projectData.product_owner?.user_id || "",
            start_date: formatDateForInput(projectData.start_date),
            due_date: formatDateForInput(projectData.due_date),
            company_id: projectData.company?.company_id || "",
            contact_id:
              projectData.billing_contact_id ||
              projectData.billing_contact?.user_id ||
              "",
            status_id: projectData.status_id || "",
            tags: projectData.tags || [],
            color: projectData.color || "#FFFFFF",
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        errorToast("Failed to load project form. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api, projectId]);

  const usersAndContacts = [
    ...users.map((u) => ({
      firstName: u.first_name,
      lastName: u.last_name,
      id: u.user_id,
    })),
    ...contacts.map((c) => ({
      firstName: c.first_name,
      lastName: c.last_name,
      id: c.user_id,
    })),
  ];

  const sortedUserContact = useMemo(() => {
    return [...usersAndContacts].sort((a, b) =>
      `${a.firstName} ${a.lastName}`.localeCompare(
        `${b.firstName} ${b.lastName}`
      )
    );
  }, [usersAndContacts]);

  const validateForm = () => {
    const errors = {};

    if (!formFields.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formFields.start_date) {
      errors.start_date = "Start date is required";
    }

    if (!formFields.status_id) {
      errors.status_id = "Status is required";
    }

    if (formFields.start_date && formFields.due_date) {
      const startDate = new Date(formFields.start_date);
      const dueDate = new Date(formFields.due_date);
      if (dueDate < startDate) {
        errors.due_date = "Due date must be after start date";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFieldUpdate = (e) => {
    const { name, value } = e.target;
    setFormFields((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleColor = (e) => {
    setFormFields((prev) => ({ ...prev, color: e }));
    if (formErrors["color"]) {
      setFormErrors((prev) => ({ ...prev, color: null }));
    }
  };

  const handleTagRemove = async (tagId) => {
    if (projectId) {
      try {
        await api.project.toggleTag(projectId, tagId);
      } catch (error) {
        console.error("Error removing tag from project:", error);
      }
    }
    setFormFields((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag.tag_id !== tagId),
    }));
  };

  const handleAddTagToObject = async (tagId) => {
    if (projectId) {
      try {
        await api.project.toggleTag(projectId, tagId);
        const existingTag = (formFields.tags || []).find(
          (tag) => tag.tag_id === tagId
        );
        if (existingTag) {
          return;
        }
        const tagResponse = await api.tag.getTagById(tagId);
        const newTag = {
          tag_id: tagResponse.tag_id,
          name: tagResponse.name,
          color: tagResponse.color,
        };
        setFormFields((prev) => ({
          ...prev,
          tags: [...(prev.tags || []), newTag],
        }));
      } catch (error) {
        console.error("Error adding tag to project:", error);
      }
    } else {
      try {
        const tagResponse = await api.tag.getTagById(tagId);
        const newTag = {
          tag_id: tagResponse.tag_id,
          name: tagResponse.name,
          color: tagResponse.color,
        };
        setFormFields((prev) => ({
          ...prev,
          tags: [...(prev.tags || []), newTag],
        }));
      } catch (error) {
        console.error("Error fetching tag:", error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const { contact_id, tags, due_date, ...baseFields } = formFields;

      const projectData = {
        name: formFields.name,
        description: formFields.description || null,
        project_manager_id: formFields.project_manager_id || null,
        product_owner_id: formFields.product_owner_id || null,
        org_id: userInfo?.org_id,
        created_by_id: userInfo?.user_id,
        status_id: formFields.status_id,
        start_date: formFields.start_date
          ? new Date(formFields.start_date + "T00:00:00").toISOString()
          : null,
        due_date: formFields.due_date
          ? new Date(formFields.due_date + "T00:00:00").toISOString()
          : null,
        ...(formFields.company_id ? { company_id: formFields.company_id } : {}),
        ...(formFields.contact_id
          ? { billing_contact_id: formFields.contact_id }
          : {}),
        color: formFields.color,
      };

      if (projectId) {
        await api.project.updateProject(projectId, projectData);
        successfulToast("Project updated successfully");
        history.push(`/project/${projectId}`);
      } else {
        const newProject = await api.project.createProject(projectData);

        if (tags && tags.length > 0 && newProject.project_id) {
          for (const tag of tags) {
            try {
              await api.project.toggleTag(newProject.project_id, tag.tag_id);
            } catch (error) {
              console.error(
                `Error adding tag ${tag.tag_id} to project:`,
                error
              );
            }
          }
        }

        successfulToast("Project created successfully");
        history.push(`/project/${newProject.project_id}`);
        return;
      }
    } catch (error) {
      console.error("Error saving project:", error);
      errorToast(
        projectId ? "Failed to update project" : "Failed to create project"
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading && projectId) {
    return <Loading />;
  }

  return (
    <div className="form-styling px-16 py-8 card">
      <h2>{projectId ? "Edit Project" : "New Project"}</h2>

      <Form onSubmit={handleSubmit}>
        <div className="form-row">
          {!projectId && (
            <div className="form-group col-md-4">
              <label htmlFor="color-picker">Project Color</label>
              <ColorPicker
                handleColor={handleColor}
                initialColor={formFields.color}
              />
            </div>
          )}
          <div className="form-group">
            <FormFieldTags
              selectedTags={formFields.tags}
              handleTagRemove={handleTagRemove}
              objectType="projects"
              objectId={projectId}
              objectPk="project_id"
              handleAddTagToObject={handleAddTagToObject}
            />
          </div>
        </div>

        <FormFieldText
          name="name"
          label="Project Name *"
          placeholder="Enter project name..."
          value={formFields.name}
          onChange={handleFieldUpdate}
          errorMessage={formErrors.name}
        />

        <div className="form-row">
          <div className="form-group col-md-4">
            <label htmlFor="start_date">Start Date</label>
            <input
              type="date"
              id="start_date"
              name="start_date"
              value={formFields.start_date}
              onChange={handleFieldUpdate}
              className={`form-control ${
                formErrors.start_date ? "is-invalid" : ""
              }`}
            />
            {formErrors.start_date && (
              <div className="invalid-feedback">{formErrors.start_date}</div>
            )}
          </div>

          <div className="form-group col-md-4">
            <label htmlFor="due_date">Due Date</label>
            <input
              type="date"
              id="due_date"
              name="due_date"
              value={formFields.due_date}
              onChange={handleFieldUpdate}
              className={`form-control ${
                formErrors.due_date ? "is-invalid" : ""
              }`}
            />
            {formErrors.due_date && (
              <div className="invalid-feedback">{formErrors.due_date}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="status_id">Status *</label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={statuses.map((status) => ({
                value: status.project_status_id,
                label: `${status.name}`,
              }))}
              value={
                statuses
                  .filter(
                    (status) =>
                      status.project_status_id === formFields.status_id
                  )
                  .map((status) => ({
                    value: status.project_status_id,
                    label: `${status.name}`,
                  }))[0] || null
              }
              onChange={(selectedOption) =>
                handleFieldUpdate({
                  target: {
                    name: "status_id",
                    value: selectedOption?.value || "",
                  },
                })
              }
              placeholder="Select a Status"
              isSearchable
            />

            {formErrors.status_id && (
              <div className="invalid-feedback">{formErrors.status_id}</div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="company_id">Company</label>
          <select
            id="company_id"
            name="company_id"
            value={formFields.company_id}
            onChange={handleFieldUpdate}
            className="form-control"
          >
            <option key="company-default" value="">
              Select a company
            </option>
            {companies.map((company, index) => (
              <option
                key={company.company_id || `company-${index}`}
                value={company.company_id}
              >
                {company.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <div className="form-group col-md-4">
            <label htmlFor="project_manager_id">Project Manager</label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={sortedUserContact.map((user) => ({
                value: user.id,
                label: `${user.firstName} ${user.lastName}`,
              }))}
              value={
                sortedUserContact
                  .filter((user) => user.id === formFields.project_manager_id)
                  .map((user) => ({
                    value: user.id,
                    label: `${user.firstName} ${user.lastName}`,
                  }))[0] || null
              }
              onChange={(selectedOption) =>
                handleFieldUpdate({
                  target: {
                    name: "project_manager_id",
                    value: selectedOption?.value || "",
                  },
                })
              }
              placeholder="Select a manager"
              isSearchable
            />
          </div>

          <div className="form-group col-md-4">
            <label htmlFor="product_owner_id">Product Owner</label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={sortedUserContact.map((user) => ({
                value: user.id,
                label: `${user.firstName} ${user.lastName}`,
              }))}
              value={
                sortedUserContact
                  .filter((user) => user.id === formFields.product_owner_id)
                  .map((user) => ({
                    value: user.id,
                    label: `${user.firstName} ${user.lastName}`,
                  }))[0] || null
              }
              onChange={(selectedOption) =>
                handleFieldUpdate({
                  target: {
                    name: "product_owner_id",
                    value: selectedOption?.value || "",
                  },
                })
              }
              placeholder="Select a product owner"
              isSearchable
            />
          </div>

          <div className="form-group col-md-4">
            <label htmlFor="contact_id">Contact</label>
            <select
              id="contact_id"
              name="contact_id"
              value={formFields.contact_id}
              onChange={handleFieldUpdate}
              className="form-control"
            >
              <option key="contact-default" value="">
                Select a contact
              </option>
              {contacts.map((contact, index) => (
                <option
                  key={contact.user_id || `contact-${index}`}
                  value={contact.user_id}
                >
                  {contact.first_name} {contact.last_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <NovelEditor
            initialValue={
              formFields.description
                ? (() => {
                    try {
                      return JSON.parse(formFields.description);
                    } catch {
                      return null;
                    }
                  })()
                : null
            }
            onChange={(jsonContent) => {
              setFormFields((prev) => ({
                ...prev,
                description: JSON.stringify(jsonContent),
              }));
            }}
          />
        </div>

        <div className="button-row">
          <div className="button-row-left">
            <Button cancel onClick={() => history.goBack()}>
              Cancel
            </Button>
          </div>
          <div className="button-row-right">
            <button type="submit" disabled={loading}>
              {loading
                ? "Saving..."
                : projectId
                  ? "Update Project"
                  : "Create Project"}
            </button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default ProjectForm;
